/**
 * Status items shown in the cruscotto (view card) and in the status detail drawer.
 *
 * Both surfaces render the same lists, so the per-mode composition lives here:
 * - External (DSLR) line: the controller ships inside the power board enclosure, so
 *   its ambient sensors (temperature, humidity, condensation risk) belong to the
 *   Controller section. Camera battery / AC
 *   power are dropped (the camera runs from the board through a dummy battery).
 * - Built-in line: the phone is both controller and camera ("capture unit"). There is
 *   never a power board, so the only thermal reading is the phone battery temperature.
 *
 * Permissions are not shown in either mode: devices are provisioned by us.
 */

import { AndroidStatus, CameraConfig, CameraMode, GpsLocation, GpsError, View } from '@/types';
import { evaluateMissionCriticalItems } from '@/components/missionCriticalConfig';
import type { StatusType } from './StatusDashboardIcon';
import {
  getMasterPowerSensors,
  SENSOR_LEVEL_STATUS,
  condensationLabel,
  condensationDetail,
  type SensorLevel,
} from './masterPowerSensors';

/** A single status entry, already resolved to its display color. */
export interface StatusDisplayItem {
  id: string;
  label: string;
  icon: string;
  textIcon?: string;
  textIconStrike?: boolean;
  status: StatusType;
  tagText: string;
  description: string;
  /** Hover tooltip for the cruscotto icon */
  tooltip?: string;
}

export interface ViewStatusItems {
  /** Controller (External) or capture unit (Built-in) section */
  controllerItems: StatusDisplayItem[];
  /** External Camera section (empty in Built-in mode) */
  cameraItems: StatusDisplayItem[];
}

interface ViewStatusInput {
  androidStatus: AndroidStatus | null | undefined;
  cameraSettings?: CameraConfig;
  cameraMode?: CameraMode;
  gpsLocation?: GpsLocation;
  gpsError?: GpsError;
  isOnline?: boolean;
  masterPower?: View['masterPower'];
}

// Mission critical item IDs per section and camera mode (order = display order)
const EXTERNAL_CONTROLLER_HEAD_IDS = ['charging', 'batteryLevel'];
const EXTERNAL_CONTROLLER_TAIL_IDS = ['networkConnection', 'gpsPosition'];
const EXTERNAL_CAMERA_IDS = ['dslrConnection', 'dslrStorage'];
const BUILTIN_CONTROLLER_IDS = ['charging', 'batteryLevel', 'batteryTemperature', 'networkConnection', 'gpsPosition'];

// Missing sensor data means the controller hardware is faulty: the customer must contact us.
const SENSOR_UNAVAILABLE_DESCRIPTION =
  'Sensor data not available. This may indicate a hardware fault — contact us at info@masterlapse.com or WhatsApp +39 346 811 8295.';

/**
 * Map issue state and severity to status type
 */
export function getStatusType(
  isIssue: boolean,
  severity: 'critical' | 'potential',
  severityOverride?: 'critical' | 'potential'
): StatusType {
  if (!isIssue) return 'success';
  const effectiveSeverity = severityOverride ?? severity;
  return effectiveSeverity === 'critical' ? 'error' : 'warning';
}

type EvaluatedItem = ReturnType<typeof evaluateMissionCriticalItems>[number];

function toDisplayItem(item: EvaluatedItem): StatusDisplayItem {
  return {
    id: item.id,
    label: item.label,
    icon: item.status.icon,
    textIcon: item.status.textIcon,
    textIconStrike: item.status.textIconStrike,
    status: getStatusType(item.status.isIssue, item.severity, item.status.severityOverride),
    tagText: item.status.tagText,
    description: item.status.description,
  };
}

function unavailableSensorItem(id: string, label: string, icon: string): StatusDisplayItem {
  return {
    id,
    label,
    icon,
    status: 'default',
    tagText: 'Unknown',
    description: SENSOR_UNAVAILABLE_DESCRIPTION,
    tooltip: `${label}: not available`,
  };
}

const TEMPERATURE_NOTE: Record<SensorLevel, string> = {
  ok: '',
  warning: ' Warmer than normal — the enclosure may be exposed to direct sunlight.',
  critical: ' Very high — risk of overheating for the camera and the controller.',
};

const HUMIDITY_NOTE: Record<SensorLevel, string> = {
  ok: '',
  warning: ' Humidity is high. Monitor the condensation risk.',
  critical: ' Humidity is very high. Moisture may form inside the enclosure.',
};

/** Ambient sensor items read by the controller: temperature, humidity, condensation. */
export function getAmbientSensorItems(mp: View['masterPower']): {
  temperature: StatusDisplayItem;
  humidity: StatusDisplayItem;
  condensation: StatusDisplayItem;
} {
  const sensors = getMasterPowerSensors(mp);
  if (!sensors) {
    return {
      temperature: unavailableSensorItem('controllerTemperature', 'Temperature', 'thermostat'),
      humidity: unavailableSensorItem('controllerHumidity', 'Humidity', 'humidity_percentage'),
      condensation: unavailableSensorItem('condensation', 'Condensation', 'blur_on'),
    };
  }

  const temp = `${sensors.temperature.toFixed(1)}°C`;
  const humidity = `${sensors.humidity.toFixed(0)}%`;
  const condensation = condensationLabel(sensors.condensation);
  return {
    temperature: {
      id: 'controllerTemperature',
      label: 'Temperature',
      icon: 'thermostat',
      status: SENSOR_LEVEL_STATUS[sensors.tempLevel],
      tagText: temp,
      description: `Ambient temperature: ${temp}.${TEMPERATURE_NOTE[sensors.tempLevel]}`,
      tooltip: `Ambient temperature: ${temp}`,
    },
    humidity: {
      id: 'controllerHumidity',
      label: 'Humidity',
      icon: 'humidity_percentage',
      status: SENSOR_LEVEL_STATUS[sensors.humidityLevel],
      tagText: humidity,
      description: `Relative humidity: ${humidity}.${HUMIDITY_NOTE[sensors.humidityLevel]}`,
      tooltip: `Humidity: ${humidity}`,
    },
    condensation: {
      id: 'condensation',
      label: 'Condensation',
      icon: sensors.condensation.level === 'ok' ? 'blur_off' : 'blur_on',
      status: SENSOR_LEVEL_STATUS[sensors.condensation.level],
      tagText: condensation,
      description: condensationDetail(sensors.condensation),
      tooltip: condensation,
    },
  };
}

/**
 * Resolve the status items of a view for its camera mode.
 */
export function getViewStatusItems({
  androidStatus,
  cameraSettings,
  cameraMode,
  gpsLocation,
  gpsError,
  isOnline,
  masterPower,
}: ViewStatusInput): ViewStatusItems {
  const evaluated = evaluateMissionCriticalItems(androidStatus, cameraSettings, gpsLocation, isOnline, gpsError);
  const pick = (ids: string[]) =>
    ids
      .map((id) => evaluated.find((item) => item.id === id))
      // Drop N/A items (e.g. DSLR storage when the camera is not connected)
      .filter((item): item is EvaluatedItem => !!item && item.status.tagText !== 'N/A')
      .map(toDisplayItem);

  if (cameraMode !== 'dslr') {
    return { controllerItems: pick(BUILTIN_CONTROLLER_IDS), cameraItems: [] };
  }

  const ambient = getAmbientSensorItems(masterPower);
  return {
    controllerItems: [
      ...pick(EXTERNAL_CONTROLLER_HEAD_IDS),
      ambient.temperature,
      ambient.humidity,
      ambient.condensation,
      ...pick(EXTERNAL_CONTROLLER_TAIL_IDS),
    ],
    cameraItems: pick(EXTERNAL_CAMERA_IDS),
  };
}
